/**
 * Runway: how far the eligible base stretches given the quota and the calendar
 * (VOX-270 — Feature 4.4).
 *
 * This is the check no competitor exposes at configuration time: dialers report
 * list penetration once the campaign is already running, and ad platforms never
 * need it because their inventory is infinite.
 *
 * Pure.
 */

import type { Pacing } from '../types';
import { activeDays, effectivePerDay, slotMinutes, type DailyPace, type PaceLimit } from './pacing';

export type RunwayStatus = 'fits' | 'short' | 'empty';

export interface Runway {
  status: RunwayStatus;
  /** Callable contacts the plan has to get through. */
  eligible: number;
  /**
   * The cap the user ASKED for. `null` when running uncapped.
   *
   * It may only appear in the sentence explaining why it is ignored ("the cap
   * of N/day doesn't fit the window"). Never in one describing what the
   * campaign will do — `perDay` is for that. It is named this way, and not
   * `contactsPerDay`, because under the old name it leaked three times into
   * sentences where it lied.
   */
  requestedPerDay: number | null;
  /**
   * The contacts that actually go out per day: the lesser of that cap and what
   * the window allows. It is the number everything else is computed from,
   * because it is the one that will happen.
   */
  perDay: number;
  /**
   * The window's ceiling, even when it isn't the one ruling: it is what turns
   * the cap into a decision rather than a guess.
   */
  capacity: number;
  /** Callable minutes an active day offers, with gaps and overlaps resolved. */
  windowMinutes: number;
  /** Which ceiling sets the rate. */
  limitedBy: PaceLimit;
  /** The campaign has no end date. */
  openEnded: boolean;
  /** Active days needed to exhaust the audience. */
  daysNeeded: number;
  /** Active days the configured range actually offers. */
  daysAvailable: number;
  /** Date the audience runs out, when it fits. "YYYY-MM-DD". */
  exhaustsOn: string | null;
  /** Active days left idle after the audience is exhausted. */
  idleDays: number;
  /** Contacts that would never be called because the range is too short. */
  leftover: number;
}

/**
 * Cross the daily quota against the eligible audience and the calendar.
 *
 * Note that removing the cap does not make the rate infinite: it raises it to
 * whatever the window allows. Assuming the whole base goes out on day one is
 * false the moment the audience exceeds what the lines can carry.
 *
 * `pace` defaults to the wizard's `effectivePerDay` (quota against the demo
 * provider ceiling). Performance passes the campaign's own quota instead
 * (`ownQuotaPace`) so a running campaign's projection never reads the demo cap.
 */
export function computeRunway(
  eligible: number,
  pacing: Pacing,
  startDate: string,
  endDate: string | null,
  pace: DailyPace = effectivePerDay(pacing),
): Runway {
  const { perDay, capacity, limitedBy } = pace;
  const available = activeDays(startDate, endDate, pacing.days);

  const base: Runway = {
    status: 'empty',
    eligible,
    requestedPerDay: pacing.contactsPerDay,
    perDay,
    capacity,
    windowMinutes: slotMinutes(pacing.slots),
    limitedBy,
    openEnded: endDate === null,
    daysNeeded: 0,
    daysAvailable: available.length,
    exhaustsOn: null,
    idleDays: 0,
    leftover: 0,
  };

  if (eligible <= 0 || perDay <= 0 || available.length === 0) return base;

  const daysNeeded = Math.ceil(eligible / perDay);

  // With no end date it never "doesn't fit": the calendar doesn't bound it, it
  // only says how long it takes. And no idle days, because there is no range
  // left over.
  if (endDate === null) {
    return {
      ...base,
      status: 'fits',
      daysNeeded,
      exhaustsOn: available[daysNeeded - 1] ?? null,
      idleDays: 0,
    };
  }

  if (daysNeeded <= available.length) {
    return {
      ...base,
      status: 'fits',
      daysNeeded,
      exhaustsOn: available[daysNeeded - 1],
      idleDays: available.length - daysNeeded,
    };
  }

  return {
    ...base,
    status: 'short',
    daysNeeded,
    leftover: eligible - available.length * perDay,
  };
}

/**
 * Suggested quota so the audience lands exactly on the last active day.
 *
 * Feeds the "apply the suggested quota" action the PRD requires to be
 * executable from the warning itself, not merely diagnosed.
 */
export function quotaToFit(eligible: number, daysAvailable: number): number {
  if (daysAvailable <= 0) return 0;
  return Math.ceil(eligible / daysAvailable);
}
